/**
 * VÉRAULT TRAVELS - GLOBAL SEARCH
 * 
 * Search across:
 * - Trip internal name
 * - Trip ID
 * - Client name
 * - Traveler name
 * - Destination
 * 
 * Results deep link to:
 * - Trip Command Center (default)
 * - Client profile (if client result selected)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, X, Plane, Users, MapPin, Loader2, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import axios from 'axios';
import { getTripStartDate, formatDate } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function VTGlobalSearch({ onResultClick }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ trips: [], clients: [], travelers: [] });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Search handler with debounce
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({ trips: [], clients: [], travelers: [] });
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/travels/search`, {
        params: { q: searchQuery }
      });
      setResults({
        trips: res.data.trips || [],
        clients: res.data.clients || [],
        travelers: res.data.travelers || []
      });
    } catch (e) {
      console.error('Search failed:', e);
      // Fallback: search trips and clients separately
      try {
        const [tripsRes, clientsRes] = await Promise.all([
          axios.get(`${API}/travels/trips`),
          axios.get(`${API}/clients`)
        ]);
        
        const q = searchQuery.toLowerCase();
        const trips = (tripsRes.data || []).filter(t => 
          t.internal_trip_name?.toLowerCase().includes(q) ||
          t.destinations?.toLowerCase().includes(q) ||
          t.id?.includes(q)
        );
        const clients = (clientsRes.data || []).filter(c => 
          c.full_name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q)
        );
        
        setResults({ trips: trips.slice(0, 5), clients: clients.slice(0, 5), travelers: [] });
      } catch (e2) {
        console.error('Fallback search failed:', e2);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (query) {
      debounceRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setResults({ trips: [], clients: [], travelers: [] });
    }
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);
  
  // Close results on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (resultsRef.current && !resultsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle result click
  const handleResultClick = (type, item) => {
    setShowResults(false);
    setQuery('');
    
    if (type === 'trip') {
      window.location.href = `/advisor/trips/${item.id}`;
    } else if (type === 'client') {
      navigate(`/advisor/manage/clients/${item.id}`);
    } else if (type === 'traveler') {
      navigate(`/advisor/manage/clients/${item.client_id || item.id}`);
    }
    
    onResultClick?.(type, item);
  };
  
  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults({ trips: [], clients: [], travelers: [] });
    inputRef.current?.focus();
  };
  
  const hasResults = results.trips.length > 0 || results.clients.length > 0 || results.travelers.length > 0;
  
  return (
    <div className="relative w-full max-w-md" data-testid="global-search">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          placeholder="Search trips, clients, destinations..."
          className="pl-10 pr-10 bg-white border-[#E6E4DE] h-10"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-4 h-4 text-[#8C8C8C]" />
          </button>
        )}
        {loading && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#C8A85A]" />
        )}
      </div>
      
      {/* Results Dropdown */}
      <AnimatePresence>
        {showResults && query && (
          <motion.div
            ref={resultsRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-[#E6E4DE] max-h-[70vh] overflow-y-auto z-50"
            data-testid="search-results"
          >
            {!hasResults && !loading ? (
              <div className="p-4 text-center text-[#8C8C8C]">
                No results found for &quot;{query}&quot;
              </div>
            ) : (
              <div className="divide-y divide-[#E6E4DE]">
                {/* Trips */}
                {results.trips.length > 0 && (
                  <div className="p-2">
                    <p className="text-xs text-[#8C8C8C] uppercase tracking-wide px-2 py-1">Trips</p>
                    {results.trips.map(trip => (
                      <button
                        key={trip.id}
                        onClick={() => handleResultClick('trip', trip)}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#F5F1EB] transition-colors text-left"
                        data-testid={`search-trip-${trip.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#8DA399]/10 flex items-center justify-center">
                          <Plane className="w-4 h-4 text-[#8DA399]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A] truncate">
                            {trip.internal_trip_name || trip.destinations}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-[#8C8C8C]">
                            {trip.destinations && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {trip.destinations}
                              </span>
                            )}
                            {getTripStartDate(trip) && (
                              <span>{formatDate(getTripStartDate(trip))}</span>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#8C8C8C]" />
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Clients */}
                {results.clients.length > 0 && (
                  <div className="p-2">
                    <p className="text-xs text-[#8C8C8C] uppercase tracking-wide px-2 py-1">Clients</p>
                    {results.clients.map(client => (
                      <button
                        key={client.id}
                        onClick={() => handleResultClick('client', client)}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#F5F1EB] transition-colors text-left"
                        data-testid={`search-client-${client.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#C8A85A]/10 flex items-center justify-center">
                          <Users className="w-4 h-4 text-[#C8A85A]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A] truncate">
                            {client.full_name}
                          </p>
                          <p className="text-xs text-[#8C8C8C] truncate">{client.email}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#8C8C8C]" />
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Travelers */}
                {results.travelers.length > 0 && (
                  <div className="p-2">
                    <p className="text-xs text-[#8C8C8C] uppercase tracking-wide px-2 py-1">Travelers</p>
                    {results.travelers.map(traveler => (
                      <button
                        key={traveler.id}
                        onClick={() => handleResultClick('traveler', traveler)}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#F5F1EB] transition-colors text-left"
                        data-testid={`search-traveler-${traveler.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Users className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A] truncate">
                            {traveler.legal_full_name || traveler.preferred_name}
                          </p>
                          <p className="text-xs text-[#8C8C8C] truncate">Traveler</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#8C8C8C]" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default VTGlobalSearch;
